import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Select, Tag } from 'antd'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BankOutlined,
  CloseOutlined,
  FileExcelOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { isHandledAuthError, municipalitiesApi } from '@/services'
import {
  exportColumnSettingsApi,
  type ExportColumnOption,
  type ExportColumnSetting,
} from '@/services/exportColumnSettings'
import { usePermission } from '@/hooks/usePermission'
import { useToast } from '@/widgets/common'

type MunicipalityOption = { id: number; name: string; code: string }

/**
 * 受給者一覧CSV出力項目設定（自治体ごとの出力列と並び）の管理画面。
 *
 * 受給者管理画面の「受給者一覧CSV出力」で出力する列を、自治体ごとに選び・並べ替える（検討事項 No.65：
 * 必要項目が随時変わるため、出力項目をコード固定から自治体設定へ移した）。
 * ADMIN は自分の自治体、SYSTEM_ADMIN は上部の「対象自治体」で選んだ自治体を維護する（決定通知書設定と同方式）。
 * 未設定の自治体は既定の項目で出力される。
 */
export default function RecipientCsvColumnSettings() {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { isSystemAdmin } = usePermission()
  const [municipalities, setMunicipalities] = useState<MunicipalityOption[]>([])
  const [targetMunicipalityId, setTargetMunicipalityId] = useState<number | null>(null)
  const [setting, setSetting] = useState<ExportColumnSetting | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isSystemAdmin) return
    municipalitiesApi
      .list()
      .then(({ data }) => setMunicipalities(data))
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('自治体一覧の取得に失敗しました')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin])

  const needsSelection = isSystemAdmin && targetMunicipalityId == null

  const load = () => {
    if (needsSelection) {
      setSetting(null)
      setSelectedKeys([])
      return
    }
    setLoading(true)
    const req = isSystemAdmin
      ? exportColumnSettingsApi.getForManagement(targetMunicipalityId as number)
      : exportColumnSettingsApi.getForMyMunicipality()
    req
      .then(({ data }) => {
        setSetting(data)
        setSelectedKeys(data?.selectedKeys ?? [])
      })
      .catch((error) => {
        if (!isHandledAuthError(error)) toast.error('CSV出力項目の取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemAdmin, targetMunicipalityId])

  const labelOf = useMemo(() => {
    const map = new Map<string, string>()
    setting?.available.forEach((c) => map.set(c.key, c.label))
    return (key: string) => map.get(key) ?? key
  }, [setting])

  const unselected: ExportColumnOption[] = useMemo(
    () => (setting?.available ?? []).filter((c) => !selectedKeys.includes(c.key)),
    [setting, selectedKeys],
  )

  const dirty = useMemo(() => {
    const current = setting?.selectedKeys ?? []
    return current.length !== selectedKeys.length || current.some((k, i) => k !== selectedKeys[i])
  }, [setting, selectedKeys])

  const add = (key: string) => setSelectedKeys((prev) => [...prev, key])
  const remove = (key: string) => setSelectedKeys((prev) => prev.filter((k) => k !== key))

  /** 出力順の入れ替え（index と index+offset を交換）。 */
  const move = (index: number, offset: number) => {
    setSelectedKeys((prev) => {
      const to = index + offset
      if (to < 0 || to >= prev.length) return prev
      const next = [...prev]
      const moved = next[index]
      next[index] = next[to]
      next[to] = moved
      return next
    })
  }

  const save = async () => {
    if (selectedKeys.length === 0) {
      toast.error('出力する項目を1つ以上選択してください')
      return
    }
    setSaving(true)
    try {
      const body = { columnKeys: selectedKeys }
      const { data } = isSystemAdmin
        ? await exportColumnSettingsApi.saveForManagement(targetMunicipalityId as number, body)
        : await exportColumnSettingsApi.saveForMyMunicipality(body)
      setSetting(data)
      setSelectedKeys(data?.selectedKeys ?? [])
      toast.success('保存しました')
    } catch (error) {
      if (!isHandledAuthError(error)) toast.error('保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: '受給者一覧CSV出力項目設定' },
        ]}
      />
      <div className="mx-auto max-w-5xl px-6 py-8">
        <BackButton onClick={() => navigate('submenu-admin')} />

        {/* ヘッダーカード */}
        <div className="mt-2 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="flex items-start gap-4 p-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-md shadow-emerald-200">
              <FileExcelOutlined style={{ fontSize: 22 }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">受給者一覧CSV出力項目設定</h1>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-500">
                受給者管理画面の「受給者一覧CSV出力」で出力する項目と、その並び順を自治体ごとに設定します。
                出力は世帯ごとに1行で、選択した項目が左から順に出力されます。
              </p>
            </div>
          </div>

          {isSystemAdmin ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3">
              <BankOutlined className="text-gray-400" />
              <span className="text-sm font-medium text-gray-600">対象自治体</span>
              <Select<number>
                style={{ minWidth: 300 }}
                placeholder="設定する自治体を選択"
                value={targetMunicipalityId ?? undefined}
                onChange={(v) => setTargetMunicipalityId(v ?? null)}
                showSearch
                optionFilterProp="label"
                options={municipalities.map((m) => ({ value: m.id, label: `${m.name}（${m.code}）` }))}
              />
            </div>
          ) : null}
        </div>

        {/* コンテンツカード */}
        <div className="mt-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          {needsSelection ? (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                <BankOutlined style={{ fontSize: 28 }} />
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-700">設定する自治体を選択してください</p>
              <p className="mt-1 max-w-md text-xs leading-relaxed text-gray-400">
                システム管理者は自治体に属さないため、上の「対象自治体」から選ぶと、その自治体のCSV出力項目を編集できます。
              </p>
            </div>
          ) : loading ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">読み込み中...</div>
          ) : setting == null ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">設定を取得できませんでした。</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">現在の状態</span>
                  {setting.customized ? (
                    <Tag color="green">自治体設定あり</Tag>
                  ) : (
                    <Tag color="default">未設定（既定の項目で出力中）</Tag>
                  )}
                </div>
                <Button type="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
                  保存
                </Button>
              </div>

              <div className="grid gap-6 px-6 py-6 md:grid-cols-2">
                {/* 出力する項目（並び順つき） */}
                <div>
                  <div className="mb-2 text-sm font-semibold text-gray-700">
                    出力する項目（上から順に出力）
                  </div>
                  {selectedKeys.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-xs text-gray-400">
                      右の「出力しない項目」から追加してください。
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl ring-1 ring-gray-100">
                      {selectedKeys.map((key, index) => (
                        <li key={key} className="flex items-center justify-between gap-2 bg-white px-3 py-2">
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="w-6 shrink-0 text-center text-xs font-semibold text-gray-400">
                              {index + 1}
                            </span>
                            <span className="truncate text-sm text-gray-700">{labelOf(key)}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1">
                            <Button
                              size="small"
                              type="text"
                              icon={<ArrowUpOutlined />}
                              disabled={index === 0}
                              onClick={() => move(index, -1)}
                            />
                            <Button
                              size="small"
                              type="text"
                              icon={<ArrowDownOutlined />}
                              disabled={index === selectedKeys.length - 1}
                              onClick={() => move(index, 1)}
                            />
                            <Button
                              size="small"
                              type="text"
                              danger
                              icon={<CloseOutlined />}
                              onClick={() => remove(key)}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* 出力しない項目 */}
                <div>
                  <div className="mb-2 text-sm font-semibold text-gray-700">出力しない項目</div>
                  {unselected.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-xs text-gray-400">
                      すべての項目が出力対象です。
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl ring-1 ring-gray-100">
                      {unselected.map((column) => (
                        <li
                          key={column.key}
                          className="flex items-center justify-between gap-2 bg-white px-3 py-2"
                        >
                          <span className="truncate text-sm text-gray-500">{column.label}</span>
                          <Button
                            size="small"
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={() => add(column.key)}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* 出力見本（CSVの見出し行） */}
              <div className="border-t border-gray-100 bg-gray-50/70 px-6 py-4">
                <div className="text-xs font-semibold text-gray-500">CSVの1行目（見出し行）</div>
                <div className="mt-1 overflow-x-auto">
                  <code className="whitespace-nowrap text-xs text-gray-700">
                    {selectedKeys.map((key) => labelOf(key)).join(',') || '（項目未選択）'}
                  </code>
                </div>
              </div>
            </>
          )}
        </div>

        <Alert
          className="mt-4"
          type="info"
          showIcon
          message={
            <span>
              設定は次回の「受給者一覧CSV出力」から反映されます。値の取得元は次のとおりで、値が無い世帯は空欄になります。
              <br />
              ・「申出者氏名／申出者フリガナ／申出者生年月日」＝世帯の申出書
              <br />
              ・「世帯主生年月日」＝受給者明細の先頭行
              <br />
              ・「口座名義人／振込先金融機関コード／支店コード／預金種目／口座番号」＝申出書の承認時に転記された口座情報
              <br />
              ・「支給日」＝その世帯が属する給付額算出対象の振込予定日
              <br />
              金融機関名・支店名は<strong>名称では保持していない</strong>ため、全銀コード（4桁／3桁）で出力されます。
            </span>
          }
        />
      </div>
    </div>
  )
}
